import {
  LinkButton,
  ToggleButtonGroup,
  ToggleButtonGroupItem,
} from "@themis/ui";
import classNames from "classnames";
import React from "react";
import { PiGridFourFill, PiListBulletsBold } from "react-icons/pi";
import { generatePath, useParams, useRouteMatch } from "react-router-dom";

import { useAccounts } from "@/api/queries/accounts";
import { Header } from "@/components/Layout/Header";
import { PageContent } from "@/components/Layout/PageContent";
import { PageLayout } from "@/components/Layout/PageLayout";
import LoadingWrapper from "@/components/loading-wrapper";
import { useSearchParams } from "@/hooks/useSearchParams";

import { ACCOUNT_ROUTES } from ".";
import { AccountCard } from "../components/AccountCard";
import { Table } from "../components/Table";
import { ACCOUNT_COLUMNS } from "../config/accountsTable";

type AccountSearchParams = {
  view?: AccountListView;
};

type AccountListView = "grid" | "table";

function AccountsContainer({ children }: { children: React.ReactNode }) {
  const [{ view }] = useSearchParams<AccountSearchParams>();

  return (
    <div
      className={classNames(
        "tw-flex tw-h-full tw-min-w-96 tw-flex-col tw-gap-4",
        {
          "tw-px-16": view === "grid",
        },
      )}
    >
      {children}
    </div>
  );
}

function MessageContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="tw-self-stretch tw-rounded-md tw-border tw-border-dashed tw-border-primaryDim-300 tw-px-3 tw-py-2">
      {children}
    </div>
  );
}

function CardsContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4 tw-self-stretch tw-py-2">
      {children}
    </div>
  );
}

export function Accounts() {
  const { url } = useRouteMatch();
  const { workspace_id } = useParams<{ workspace_id: string }>();
  const { isLoading, data, error } = useAccounts(Number(workspace_id));
  const [{ view }, setSearchParams] = useSearchParams<AccountSearchParams>();

  const accountsListView = view || "table";

  function handleViewChange(newView: AccountListView) {
    setSearchParams({ view: newView }, true);
  }

  return (
    <PageLayout>
      <Header
        title="Accounts"
        HeaderOptions={
          <ToggleButtonGroup
            size="lg"
            type="single"
            value={accountsListView}
            onValueChange={handleViewChange}
          >
            <ToggleButtonGroupItem value="grid">
              <PiGridFourFill className="tw-h-[18px] tw-w-[18px]" />
            </ToggleButtonGroupItem>
            <ToggleButtonGroupItem value="table">
              <PiListBulletsBold className="tw-h-[18px] tw-w-[18px]" />
            </ToggleButtonGroupItem>
          </ToggleButtonGroup>
        }
      />
      <PageContent>
        <AccountsContainer>
          <MessageContainer>
            <p className="tw-text-sm tw-font-semibold tw-text-primaryDim-300">
              Accounts are business entities that are connected throughout the
              platform.
            </p>
          </MessageContainer>
          <LoadingWrapper
            loadingLayout={accountsListView}
            loading={isLoading}
            errorText={
              error ? "There was an issue loading the accounts data." : ""
            }
          >
            {accountsListView === "grid" ? (
              <>
                <CardsContainer>
                  {data?.accounts?.map(
                    ({ id, logo, account_types, name, is_active }) => (
                      <AccountCard
                        linkTo={generatePath(
                          `${url}${ACCOUNT_ROUTES.details}`,
                          { accountId: id },
                        )}
                        key={id}
                        id={id}
                        isActive={is_active}
                        logoURL={logo?.file_url}
                        name={name}
                        type={account_types[0]}
                      />
                    ),
                  )}
                </CardsContainer>
                <LinkButton
                  to={generatePath(`${url}${ACCOUNT_ROUTES.details}`, {
                    accountId: "new",
                  })}
                >
                  Add New Account
                </LinkButton>
              </>
            ) : (
              <Table
                rows={data?.accounts}
                columns={ACCOUNT_COLUMNS}
                addRowText="Add New Account +"
              />
            )}
          </LoadingWrapper>
        </AccountsContainer>
      </PageContent>
    </PageLayout>
  );
}
